import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  VideoCamera, Star, Check, Spinner, ShieldCheck, Crown,
  CalendarBlank, Clock, Phone, Sparkle, Compass, Lightning,
} from "@phosphor-icons/react";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { createRazorpayCheckout } from "../lib/razorpayOptions";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT; s.async = true;
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const HIGHLIGHTS = [
  { icon: VideoCamera, title: "60-min private Zoom session", body: "Face-to-face with AstroSachin — no scripts, just your chart" },
  { icon: Compass,     title: "Deep chart reading",           body: "Career, marriage, wealth, remedies, dashas — go anywhere you want" },
  { icon: Sparkle,     title: "Personalised remedies",         body: "Mantras, gems, donations, rudraksha — what actually works for YOUR chart" },
  { icon: ShieldCheck, title: "Confidential & recorded (optional)", body: "Ask about anything — your session stays private" },
];

const COVERAGE = [
  "Life direction & purpose (D1 + D9 synthesis)",
  "Career & finances (D10 + Rahu/Saturn placements)",
  "Marriage & relationships (7th house + Venus / Jupiter transits)",
  "Health & vitality (6th/8th + current mahadasha stress)",
  "Timing of key events (Vimshottari + upcoming antardashas)",
  "Custom remedies you can start same day",
];

const FAQS = [
  { q: "How does the session happen?", a: "You'll receive a Zoom link + calendar invite by email within 24 hours of booking. The session is 60 minutes, one-on-one, and lets you go as deep as you want on any life topic." },
  { q: "Do I need to share my birth details?", a: "Yes — accurate date, time and place of birth are essential. Fill them in the form below and AstroSachin will have your chart ready before we meet." },
  { q: "What if I need to reschedule?", a: "Free reschedule up to 24 hours before the session — just reply to the confirmation email. Within 24 hours we can still adjust, subject to calendar availability." },
  { q: "Refund policy?", a: "If we haven't scheduled yet or if the reading doesn't happen for any reason on AstroSachin's side, you'll receive a 100% refund. Once the session is complete, no refunds." },
];

const PRICE_USD = 120;
const SACHIN_AVATAR = "https://customer-assets.emergentagent.com/job_astro-connect-42/artifacts/znrurs52_C9325BD4-50DF-46B8-A4DF-A303F39CB1E9.PNG";

export default function PersonalZoomMeetingPage() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    dob: "",
    tob: "",
    place: "",
    timezone: "Asia/Kolkata",
    question: "",
    preferredSlots: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const canSubmit =
    !!user &&
    form.name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.dob && form.tob && form.place.trim() &&
    form.question.trim().length >= 15 &&
    form.preferredSlots.trim().length >= 5;

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/personal-zoom-meeting";
    startGoogleSignIn(redirectUrl);
  };

  const handlePay = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load Razorpay. Please check your connection.");
        setSubmitting(false); return;
      }
      const { data: order } = await api.post("/personal-zoom/create-order", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        birth_details: {
          date: form.dob, time: form.tob, place: form.place.trim(), timezone: form.timezone,
        },
        question: form.question.trim(),
        preferred_slots: form.preferredSlots.trim(),
      });
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "AstroKuberChat · Personal Zoom Reading",
        description: "60-min 1:1 with AstroSachin",
        image: "/favicon.ico",
        prefill: order.prefill,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            const { data: result } = await api.post("/personal-zoom/verify-payment", {
              booking_id: order.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({ booking_id: result.booking_id, payment_id: result.payment_id });
            toast.success("Booked! You'll receive a Zoom link within 24 hours.");
            checkAuth?.();
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              window.gtag("event", "conversion_event_purchase", {
                value: PRICE_USD, currency: "USD",
                transaction_id: result.booking_id,
                item_category: "personal_zoom_reading",
              });
            }
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed. Contact support.");
          } finally { setSubmitting(false); }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      };
      const rzp = createRazorpayCheckout(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setSubmitting(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="pz-success-page">
        <Header />
        <main className="flex-1 max-w-xl mx-auto w-full px-4 sm:px-6 py-14 text-center">
          <div className="relative mx-auto w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 animate-pulse" />
            <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
              <Check size={36} weight="bold" className="text-emerald-600" />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Booking confirmed</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">You&apos;re on the calendar</h1>
          <p className="text-muted mt-4">
            Your <strong>60-minute Personal Zoom Reading</strong> with AstroSachin is confirmed.
            You&apos;ll receive a Zoom link + calendar invite within 24 hours at {form.email}.
          </p>
          <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-amber-50 via-rose-50 to-fuchsia-50 border border-amber-200 text-left text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted">Booking ID</span><span className="font-mono font-semibold text-[#3F2D1B]" data-testid="pz-booking-id">{success.booking_id}</span></div>
            {success.payment_id && (<div className="flex justify-between"><span className="text-muted">Payment ID</span><span className="font-mono text-[#3F2D1B]">{success.payment_id}</span></div>)}
          </div>
          <Link to="/" className="inline-block mt-8 rounded-full px-6 py-2.5 bg-[#3F2D1B] text-white text-sm">Back to home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="personal-zoom-page">
      <Header />
      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0f0a] via-[#3F2D1B] to-[#6b3f1b]" />
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 25% 20%, #D4AF37 0%, transparent 40%), radial-gradient(circle at 80% 60%, #F59E0B 0%, transparent 45%)" }} />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 text-amber-50">
            <div className="flex items-start gap-5 flex-wrap">
              {/* Profile pic */}
              <img
                src={SACHIN_AVATAR}
                alt="AstroSachin"
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-4 ring-amber-400/60 shadow-2xl shrink-0"
                data-testid="pz-astrologer-avatar"
              />
              <div className="flex-1 min-w-[280px]">
                <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-black/30 px-3 py-1 text-[10px] uppercase tracking-widest text-amber-300">
                  <Crown size={12} weight="fill" /> With AstroSachin
                </div>
                <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight font-light">
                  Personal Zoom Reading
                </h1>
                <p className="mt-4 max-w-2xl text-amber-100/85 text-base sm:text-lg leading-relaxed font-light">
                  A private, unrushed 60-minute 1:1 with AstroSachin, founder of AstroKuberChat.
                  We&apos;ll go deep into your chart — career, marriage, wealth, health,
                  upcoming dashes, remedies — anywhere you want to take it.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 border border-amber-400/50 px-4 py-2 backdrop-blur">
                    <Clock size={16} weight="fill" className="text-amber-300" />
                    <span className="text-amber-100 text-sm">60 min · Zoom</span>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 border border-amber-400/50 px-4 py-2 backdrop-blur">
                    <span className="text-amber-100 text-sm">USD ${PRICE_USD}</span>
                  </div>
                  <a href="#book" className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#3F2D1B] px-5 py-2.5 text-sm font-semibold hover:bg-amber-300 transition-colors" data-testid="pz-hero-cta">
                    Book my session <VideoCamera size={16} weight="fill" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="grid sm:grid-cols-2 gap-3">
            {HIGHLIGHTS.map((h, i) => {
              const I = h.icon;
              return (
                <div key={i} className="rounded-2xl border border-amber-200/50 bg-white p-4 flex gap-3 items-start" data-testid={`pz-highlight-${i}`}>
                  <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <I size={20} weight="duotone" className="text-amber-700" />
                  </div>
                  <div>
                    <div className="font-serif-display text-base text-[#3F2D1B]">{h.title}</div>
                    <p className="text-[13px] text-muted leading-relaxed mt-0.5">{h.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* WHAT WE'LL COVER */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B]">What we&apos;ll cover</h2>
          <p className="text-muted text-sm mt-1">Bring one big question or many small ones — this session is 100% yours.</p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2">
            {COVERAGE.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#3F2D1B]">
                <Check size={16} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* BOOKING FORM */}
        <section id="book" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="rounded-3xl border border-amber-200 bg-white p-5 sm:p-7 shadow-[0_4px_24px_rgba(63,45,27,0.05)]" data-testid="pz-book-panel">
            <h2 className="font-serif-display text-2xl text-[#3F2D1B]">Book your session</h2>
            <p className="text-muted text-sm mt-1">USD ${PRICE_USD} · 60 min · you&apos;ll get a Zoom link within 24 hours.</p>

            {!user ? (
              <button onClick={handleLogin} className="mt-6 btn-primary rounded-full px-6 py-2.5 text-sm font-medium" data-testid="pz-signin-btn">
                Sign in to continue
              </button>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="label-eyebrow text-xs">Full name</span>
                  <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pz-name" />
                </label>
                <label className="block">
                  <span className="label-eyebrow text-xs">Email</span>
                  <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pz-email" />
                </label>
                <label className="block">
                  <span className="label-eyebrow text-xs">Phone (with country code)</span>
                  <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98…" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pz-phone" />
                </label>
                <label className="block">
                  <span className="label-eyebrow text-xs">Date of birth</span>
                  <input type="date" value={form.dob} onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pz-dob" />
                </label>
                <label className="block">
                  <span className="label-eyebrow text-xs">Time of birth</span>
                  <input type="time" value={form.tob} onChange={(e) => setForm(f => ({ ...f, tob: e.target.value }))} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="pz-tob" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="label-eyebrow text-xs">Place of birth</span>
                  <div className="mt-1.5">
                    <PlaceAutocompleteInput
                      value={form.place}
                      onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                      onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))}
                      placeholder="City, Country"
                      testId="pz-place"
                    />
                  </div>
                </label>
                <label className="block sm:col-span-2">
                  <span className="label-eyebrow text-xs">Your key question (min 15 chars)</span>
                  <textarea value={form.question} onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))} rows={3} placeholder="What's on your mind that you want the chart to answer? Career direction, marriage timing, a specific dasha you're worried about, remedies for a stuck situation…" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg text-sm" data-testid="pz-question" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="label-eyebrow text-xs">Preferred 2-3 time slots (with timezone)</span>
                  <textarea value={form.preferredSlots} onChange={(e) => setForm(f => ({ ...f, preferredSlots: e.target.value }))} rows={2} placeholder="e.g. Wed 27 Nov 6-7 PM IST, Fri 29 Nov 9-10 AM IST, Sun 1 Dec 4-5 PM IST" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg text-sm" data-testid="pz-slots" />
                </label>

                <div className="sm:col-span-2 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-[#3F2D1B]">
                  <div className="flex justify-between font-medium"><span>Personal Zoom Reading · 60 min</span><span>USD ${PRICE_USD}</span></div>
                  <div className="text-[11px] text-muted mt-1">Payments processed securely by Razorpay (International Payments — cards / UPI / netbanking).</div>
                </div>

                <div className="sm:col-span-2">
                  <button
                    disabled={!canSubmit || submitting}
                    onClick={handlePay}
                    className="w-full sm:w-auto rounded-full px-6 py-3 text-sm font-semibold bg-[#3F2D1B] text-white hover:bg-[#5a3e22] disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="pz-pay-btn"
                  >
                    {submitting
                      ? (<><Spinner size={16} className="animate-spin" /> Opening secure payment…</>)
                      : (<><Lightning size={16} weight="fill" /> Pay USD ${PRICE_USD} & book</>)}
                  </button>
                  {!canSubmit && (
                    <div className="mt-2 text-[11px] text-muted">Fill all fields (name, email, phone, DOB/TOB/place, question, preferred slots).</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B]">Frequently asked</h2>
          <div className="mt-4 space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="rounded-xl border border-amber-200 bg-white p-4" data-testid={`pz-faq-${i}`}>
                <summary className="cursor-pointer text-sm font-medium text-[#3F2D1B]">{f.q}</summary>
                <p className="mt-2 text-sm text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
